import { useState } from 'react';
import { MapPin, Check, Pencil, Trash2 } from 'lucide-react';
import Badge from './ui/Badge';
import Button from './ui/Button';

function formatAddress(address) {
  return [address.building, address.address_line, address.location].filter(Boolean).join(', ');
}

export default function AddressCard({
  address,
  selectable = false,
  selected = false,
  onSelect,
  onEdit,
  onDelete,
  onSetDefault,
  busy = false,
}) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const highlighted = selectable && selected;

  return (
    <div
      onClick={selectable ? onSelect : undefined}
      className={[
        'rounded-lg border bg-white p-4 flex flex-col gap-2 transition-all duration-200',
        selectable ? 'cursor-pointer' : '',
        highlighted ? 'border-terra-400 ring-2 ring-terra-400/20' : 'border-sand-200',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {selectable && (
            <span
              className={[
                'shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center',
                highlighted ? 'border-terra-400 bg-terra-400' : 'border-sand-300',
              ].join(' ')}
            >
              {highlighted && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
            </span>
          )}
          <MapPin className="w-4 h-4 text-ink-400 shrink-0" />
          <p className="text-sm font-semibold text-ink-900 truncate">
            {address.label || address.location}
          </p>
        </div>
        {address.is_default && <Badge variant="accepted">Default</Badge>}
      </div>

      <p className="text-sm text-ink-400 pl-6">{formatAddress(address)}</p>

      {!selectable && (
        <div className="pl-6 pt-1">
          {confirmingDelete ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-ink-700">Delete this address?</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setConfirmingDelete(false)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => onDelete?.()}
                loading={busy}
              >
                Confirm
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onEdit}
                className="inline-flex items-center gap-1 text-xs font-medium text-ink-700 hover:text-terra-400 transition-colors duration-150"
              >
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
              {!address.is_default && (
                <button
                  type="button"
                  onClick={() => onSetDefault?.()}
                  className="text-xs font-medium text-ink-700 hover:text-terra-400 transition-colors duration-150"
                >
                  Set as default
                </button>
              )}
              <button
                type="button"
                onClick={() => setConfirmingDelete(true)}
                className="inline-flex items-center gap-1 text-xs font-medium text-uae-red hover:text-uae-red/80 transition-colors duration-150"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
